import { useCallback, useEffect, useId, useRef, type CSSProperties, type ReactNode } from 'react'
import { ChevronLeftIcon, CloseIcon } from '../components/icons'
import { useFocusTrap } from '../hooks/useFocusTrap'
import { useScrollLock } from '../hooks/useScrollLock'
import type { DisplayMode } from '../types'

export interface ShellProgress {
  /** 1-based index of the current step. */
  current: number
  total: number
  label: string
}

export interface ShellProps {
  displayMode: DisplayMode
  open: boolean
  onClose: () => void
  title: string
  subtitle?: string | null
  logoUrl?: string | null
  themeStyle: CSSProperties
  themeMode: 'light' | 'dark'
  progress?: ShellProgress | null
  /**
   * Identifies the screen inside the body. Changing it replays the transition,
   * so the widget reads as moving between steps rather than redrawing.
   */
  stepKey?: string
  /** Which way the customer is going, so the movement matches the intent. */
  direction?: 'forward' | 'back'
  /**
   * Goes back a step. Rendered in the header rather than as a row above the
   * primary button: on a phone that row cost 44 vertical pixels on every screen
   * to hold the one control nobody is looking for.
   */
  onBack?: (() => void) | null
  /** Sticky action bar, pinned below the scrolling body. */
  actions?: ReactNode
  footer?: ReactNode
  children: ReactNode
}

/** The progress bar's accessible name: "Step 2 of 4, What you need". */
export function progressName(progress: ShellProgress): string {
  return `Step ${progress.current} of ${progress.total}, ${progress.label}`
}

/** Overlay shells float above the host page; embedded shells sit in its flow. */
function isOverlayMode(mode: DisplayMode): boolean {
  return mode === 'drawer' || mode === 'modal'
}

/**
 * The presentation layer. Owns chrome, motion and every accessibility
 * behaviour an overlay needs; knows nothing about booking.
 *
 * Display mode is configuration, not a fork: the same children render in a
 * drawer, a modal, an inline block or a hosted page.
 */
export function Shell({
  displayMode,
  open,
  onClose,
  title,
  subtitle,
  logoUrl,
  themeStyle,
  themeMode,
  progress,
  stepKey,
  direction,
  onBack,
  actions,
  footer,
  children,
}: ShellProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const overlay = isOverlayMode(displayMode)

  // Embedded modes are always "open" — there is nothing to dismiss.
  const isVisible = overlay ? open : true

  const handleEscape = useCallback(() => {
    if (overlay) onClose()
  }, [onClose, overlay])

  useScrollLock(overlay && open)
  useFocusTrap(panelRef, overlay && open, { onEscape: handleEscape })

  // Guard against a host page that unmounts us mid-transition.
  useEffect(() => {
    if (!overlay || open) return
    panelRef.current?.blur()
  }, [open, overlay])

  // Unmounted when closed, so nothing inside stays in the tab order.
  if (!isVisible) return null

  const panel = (
    <div
      ref={panelRef}
      className={`ac-panel ac-panel--${displayMode}`}
      role={overlay ? 'dialog' : 'region'}
      aria-modal={overlay ? true : undefined}
      aria-labelledby={titleId}
    >
      <header className="ac-header">
        {onBack ? (
          <button type="button" className="ac-icon-btn" onClick={onBack} aria-label="Go back">
            <ChevronLeftIcon />
          </button>
        ) : null}
        {logoUrl ? <img className="ac-header__logo" src={logoUrl} alt="" /> : null}
        <div className="ac-header__titles">
          <h2 className="ac-header__title" id={titleId}>
            {title}
          </h2>
          {progress ? (
            <p className="ac-header__subtitle">
              Step {progress.current} of {progress.total} · {progress.label}
            </p>
          ) : subtitle ? (
            <p className="ac-header__subtitle">{subtitle}</p>
          ) : null}
        </div>
        {overlay ? (
          <button type="button" className="ac-icon-btn" onClick={onClose} aria-label="Close booking">
            <CloseIcon />
          </button>
        ) : null}
      </header>

      {progress ? (
        <div
          className="ac-progress"
          role="progressbar"
          aria-label={progressName(progress)}
          aria-valuemin={1}
          aria-valuemax={progress.total}
          aria-valuenow={progress.current}
          aria-valuetext={`Step ${progress.current} of ${progress.total}`}
        >
          {Array.from({ length: progress.total }, (_, index) => (
            <span
              key={index}
              className={`ac-progress__segment${
                index < progress.current ? ' ac-progress__segment--done' : ''
              }`}
            />
          ))}
        </div>
      ) : null}

      <div className="ac-body">
        <div key={stepKey} className={`ac-step ac-step--${direction ?? 'forward'}`}>
          {children}
        </div>
      </div>

      {actions ? <div className="ac-actions">{actions}</div> : null}
      {footer ? <footer className="ac-footer">{footer}</footer> : null}
    </div>
  )

  if (!overlay) {
    return (
      <div style={themeStyle} data-ac-theme={themeMode}>
        {panel}
      </div>
    )
  }

  return (
    <div style={themeStyle} data-ac-theme={themeMode}>
      {/* Presentational: Escape and the labelled close button do the real work. */}
      <div className="ac-overlay" onClick={onClose} aria-hidden="true" />
      {panel}
    </div>
  )
}
